import { useState, useEffect } from 'react';
import { Download, Check, Archive } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';

const formatMB = (bytes) => (bytes > 0 ? `≈ ${Math.max(1, Math.round(bytes / (1024 * 1024)))} MB` : '');

const triggerDownload = (url, filename) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const LINK_TTL_MS = 40 * 60 * 1000;

export const ZipPartsDialog = ({ data, onClose, onRefresh }) => {
  const [done, setDone] = useState([]);
  const [current, setCurrent] = useState(null);

  useEffect(() => { setDone([]); setCurrent(data); }, [data]);

  const parts = current?.parts || [];

  const download = async (part) => {
    let target = part;
    if (Date.now() - (current.fetchedAt || 0) > LINK_TTL_MS && onRefresh) {
      const fresh = await onRefresh();
      if (fresh) {
        setCurrent(fresh);
        target = fresh.parts.find((p) => p.part === part.part) || part;
      }
    }
    triggerDownload(target.download_url, target.filename);
    setDone((prev) => (prev.includes(part.part) ? prev : [...prev, part.part]));
  };

  return (
    <Dialog open={!!data} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="zip-parts-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{data?.title}</DialogTitle>
          <DialogDescription>
            Tu galería tiene {data?.image_count} fotos y se ha dividido en {parts.length} archivos ZIP
            para que la descarga sea completa y fiable. Descarga cada parte:
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          {parts.map((part) => {
            const isDone = done.includes(part.part);
            return (
              <div key={part.part} className="flex items-center justify-between gap-3 border border-border p-3" data-testid={`zip-part-row-${part.part}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <Archive className="w-5 h-5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Parte {part.part} de {parts.length}</p>
                    <p className="text-xs text-muted-foreground">{part.image_count} fotos {formatMB(part.size_bytes)}</p>
                  </div>
                </div>
                <Button size="sm" variant={isDone ? 'outline' : 'default'} className="gap-2 shrink-0" onClick={() => download(part)} data-testid={`zip-part-download-${part.part}`}>
                  {isDone ? <Check className="w-4 h-4" /> : <Download className="w-4 h-4" />}
                  {isDone ? 'Descargada' : 'Descargar'}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-2" data-testid="zip-parts-progress">
          {done.length} de {parts.length} partes descargadas
        </p>
      </DialogContent>
    </Dialog>
  );
};

export { triggerDownload };
